import React, { useState, useEffect } from 'react';
import { MessageCircle, Copy, Check } from 'lucide-react';
import {
  gerarLink, redefinirSenha, linkDoCliente, revogarLink,
  urlDoLink, mensagemDoLink, linkDoWhatsapp,
} from '../../../data/cliente';
import {
  gerarLinkDoTecnico, redefinirSenhaDoTecnico, linkDoTecnico, mensagemDoLinkDoTecnico,
} from '../../../data/tecnico';

/**
 * Link de acompanhamento de um cliente: gerar, mandar, revogar.
 *
 * O painel existe para uma ação só — pôr link e senha no WhatsApp do cliente —
 * e por isso os dois ficam visíveis lado a lado, cada um com o seu botão de
 * copiar, com o "Enviar no WhatsApp" como caminho curto. Validade e aberturas
 * ficam à vista o tempo todo: quem manda precisa enxergar o que está
 * entregando, e revogar sem procurar onde.
 */

// Sem O/0, I/1, S/5: a senha vai ser lida em voz alta no telefone e digitada
// num celular sujo de barro. Ambiguidade aqui vira ligação para o laboratório.
const ALFABETO = 'ABCDEFGHJKLMNPQRTUVWXYZ23456789';

const senhaSugerida = () => {
  const n = new Uint32Array(6);
  (window.crypto || window.msCrypto).getRandomValues(n);
  return Array.from(n, (v) => ALFABETO[v % ALFABETO.length]).join('');
};

/**
 * Cliente e técnico usam o mesmo painel: a mesma tabela de links, a mesma
 * senha, o mesmo envio. Muda de onde vem o link e o que a mensagem diz.
 * O técnico guarda o telefone em `contact`, que é texto livre — se não for um
 * número, o WhatsApp abre para escolher o contato.
 */
const PORTAS = {
  cliente: {
    buscar: linkDoCliente, gerar: gerarLink, redefinir: redefinirSenha,
    mensagem: (nome, url, senha) => mensagemDoLink({ cliente: nome, url, senha }),
    telefone: (dono) => dono.phone,
    rotuloDoBotao: 'Gerar link de acompanhamento',
    rotuloDaSenha: 'Senha de acesso do cliente',
    quemSabe: 'o cliente',
  },
  tecnico: {
    buscar: linkDoTecnico, gerar: gerarLinkDoTecnico, redefinir: redefinirSenhaDoTecnico,
    mensagem: (nome, url, senha) => mensagemDoLinkDoTecnico({ tecnico: nome, url, senha }),
    telefone: (dono) => dono.contact,
    rotuloDoBotao: 'Gerar link da agenda',
    rotuloDaSenha: 'Senha de acesso do técnico',
    quemSabe: 'o técnico',
  },
};

const LinkDoCliente = ({ client, tecnico }) => {
  const dono = tecnico || client;
  const porta = PORTAS[tecnico ? 'tecnico' : 'cliente'];
  const [link, setLink] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [copiado, setCopiado] = useState(null);
  const [erro, setErro] = useState(null);
  // null | 'gerar' | 'trocar' — mesmo campo, ações diferentes.
  const [compondo, setCompondo] = useState(null);
  const [senha, setSenha] = useState('');
  // Guardada só na memória desta tela: o banco só tem o hash. Fechou a tela,
  // some — e aí o caminho é "Trocar senha", que mantém o endereço.
  const [senhaNova, setSenhaNova] = useState(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    let cancelado = false;
    porta.buscar(dono.id)
      .then(l => { if (!cancelado) setLink(l); })
      .catch(() => { if (!cancelado) setLink(null); })
      .finally(() => { if (!cancelado) setCarregando(false); });
    return () => { cancelado = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dono.id]);

  const abrirComposicao = (modo) => {
    setErro(null);
    setSenha(senhaSugerida());
    setCompondo(modo);
  };

  const confirmar = async () => {
    const limpa = senha.trim();
    if (limpa.length < 4) { setErro('A senha precisa de ao menos 4 caracteres.'); return; }
    setErro(null);
    setSalvando(true);
    try {
      if (compondo === 'trocar') {
        await porta.redefinir(dono.id, limpa);
      } else {
        await porta.gerar(dono.id, limpa, 180);
      }
      setLink(await porta.buscar(dono.id));
      setSenhaNova(limpa);
      setCompondo(null);
    } catch (e) {
      console.error('Erro no link do cliente:', e);
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const copiar = async (texto, qual) => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(qual);
      setTimeout(() => setCopiado(null), 2500);
    } catch {
      // Sem permissão de área de transferência (navegador antigo, http): o
      // texto já está à vista no painel para copiar à mão, em vez de sumir.
      setErro('Selecione e copie o texto acima.');
    }
  };

  const revogar = async () => {
    if (!window.confirm(
      `Revogar o link de ${dono.name}?\n\nQuem já entrou perde o acesso imediatamente.`
    )) return;
    try {
      await revogarLink(link.id);
      setLink(null);
      setSenhaNova(null);
    } catch (e) {
      setErro(e.message);
    }
  };

  const trocar = () => {
    if (!window.confirm(
      'Trocar a senha deste link?\n\nO endereço continua o mesmo, mas quem já entrou precisa digitar a senha nova.'
    )) return;
    abrirComposicao('trocar');
  };

  if (carregando) return null;

  const url = link ? urlDoLink(link.token) : null;
  const mensagem = link
    ? porta.mensagem(dono.name, url, senhaNova)
    : null;
  const vencido = link?.expira_em && new Date(link.expira_em) < new Date();
  const acaoFraca = 'text-sm text-slate-500 hover:text-slate-800 underline';

  const enviar = () => {
    window.open(linkDoWhatsapp(porta.telefone(dono), mensagem), '_blank', 'noopener,noreferrer');
  };

  /* ---------------- composição da senha ---------------- */
  if (compondo) {
    return (
      <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
        <label className="block text-xs text-slate-500">
          {compondo === 'trocar' ? 'Nova senha de acesso' : porta.rotuloDaSenha}
        </label>
        <div className="flex gap-2">
          <input
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && confirmar()}
            autoFocus
            className="flex-1 min-w-0 px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono tracking-wide
                       focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button onClick={confirmar} disabled={salvando}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:bg-slate-300">
            {salvando ? 'Salvando...' : 'Confirmar'}
          </button>
          <button onClick={() => { setCompondo(null); setErro(null); }} className={acaoFraca}>
            Cancelar
          </button>
        </div>
        <p className="text-xs text-slate-500">
          Sugerimos uma, mas pode trocar por algo que {porta.quemSabe} já saiba.
        </p>
        {erro && <p className="text-xs text-slate-600">{erro}</p>}
      </div>
    );
  }

  /* ---------------- sem link ---------------- */
  if (!link) {
    return (
      <div className="mt-3 pt-3 border-t border-slate-100">
        <button onClick={() => abrirComposicao('gerar')}
          className="text-sm text-blue-600 hover:text-blue-800 underline">
          {porta.rotuloDoBotao}
        </button>
        {erro && <p className="text-xs text-slate-600 mt-1">{erro}</p>}
      </div>
    );
  }

  /* ---------------- painel de envio ---------------- */
  return (
    <div className="mt-3 pt-3 border-t border-slate-100 space-y-2.5">
      <div className="rounded-lg border border-slate-200 bg-slate-50 divide-y divide-slate-200">
        <div className="flex items-center gap-2 px-3 py-2">
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-wide text-slate-400">Link</p>
            <p className="text-xs text-slate-700 break-all leading-snug">{url}</p>
          </div>
          <button onClick={() => copiar(url, 'link')} title="Copiar link"
            className="p-2 text-slate-400 hover:text-blue-600 shrink-0">
            {copiado === 'link' ? <Check size={16} className="text-blue-600" /> : <Copy size={16} />}
          </button>
        </div>

        <div className="flex items-center gap-2 px-3 py-2">
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-wide text-slate-400">Senha</p>
            {senhaNova ? (
              <p className="text-sm font-mono tracking-widest text-slate-900 leading-snug">{senhaNova}</p>
            ) : (
              /* O banco guarda só o hash. Dizer "•••" e oferecer a troca é a
                 verdade; fingir que dá para consultar faria a pessoa descobrir
                 o contrário na hora em que o cliente ligasse sem conseguir
                 entrar. */
              <p className="text-xs text-slate-500 leading-snug">
                definida antes e não fica visível —{' '}
                <button onClick={trocar} className="text-blue-600 hover:text-blue-800 underline">
                  trocar senha
                </button>
              </p>
            )}
          </div>
          {senhaNova && (
            <button onClick={() => copiar(senhaNova, 'senha')} title="Copiar senha"
              className="p-2 text-slate-400 hover:text-blue-600 shrink-0">
              {copiado === 'senha' ? <Check size={16} className="text-blue-600" /> : <Copy size={16} />}
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={enviar}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm
                     hover:bg-blue-700">
          <MessageCircle size={15} />
          Enviar no WhatsApp
        </button>
        <button onClick={() => copiar(mensagem, 'tudo')}
          className="px-3 py-2 rounded-lg border border-slate-200 text-slate-700 text-sm hover:bg-slate-50">
          {copiado === 'tudo' ? 'Mensagem copiada!' : 'Copiar mensagem'}
        </button>
      </div>

      {/* O número vem do cadastro. Sem número, o WhatsApp abre sem
          destinatário — melhor escolher na mão do que mandar a senha de um
          cliente para o telefone de outro. */}
      {!porta.telefone(dono) && (
        <p className="text-xs text-slate-500">
          Sem telefone no cadastro: o WhatsApp vai abrir para você escolher o contato.
        </p>
      )}
      {!senhaNova && (
        <p className="text-xs text-slate-500">
          A mensagem vai só com o link. Troque a senha para mandá-la junto.
        </p>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={trocar} className={acaoFraca}>Trocar senha</button>
        <button onClick={() => abrirComposicao('gerar')} className={acaoFraca}>Gerar novo link</button>
        <button onClick={revogar} className="text-sm text-slate-500 hover:text-red-600 underline">
          Revogar
        </button>
      </div>

      <p className="text-xs text-slate-500">
        {vencido
          ? 'Vencido — gere um novo.'
          : link.expira_em
            ? `Vale até ${new Date(link.expira_em).toLocaleDateString('pt-BR')}`
            : 'Sem validade definida'}
        {' · '}
        {/* Aberturas contam entradas com senha certa — e denunciam um link que
            circulou mais do que devia. */}
        {link.aberturas > 0
          ? `aberto ${link.aberturas}× · último em ${new Date(link.visto_em).toLocaleDateString('pt-BR')}`
          : 'ainda não aberto'}
      </p>

      {erro && <p className="text-xs text-slate-600">{erro}</p>}
    </div>
  );
};

export default LinkDoCliente;
